import React, { createContext, useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import { ExternalToast, Toaster as Sonner, toast as sonnerToast } from 'sonner';

import { Icon } from './Icon';
import { textStyles } from './TextStyles';
import { tv } from './cn';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const styles = tv({
  slots: {
    toaster:
      'group toaster [&>li]:border-border-secondary [&>li]:bg-bg-secondary [&>li]:text-text-primary [&>li]:shadow-md',
    toast: '',
    icon: 'm-0 size-5',
    title: textStyles.body({
      size: 'md',
      weight: 'semibold',
      className: 'group-[.toaster]:text-inherit',
    }),
    description: textStyles.body({
      size: 'md',
      weight: 'regular',
      className: 'group-[.toaster]:text-inherit',
    }),
    actionButton:
      'group-[.toast]:bg-bg-accent-strong-primary group-[.toast]:text-text-accent-strong',
    cancelButton:
      'group-[.toast]:bg-bg-quaternary group-[.toast]:text-text-tertiary',
    closeButton:
      '-right-4 left-auto top-2 -m-2 size-8 rounded-full border-8 border-transparent bg-clip-padding p-px group-[.toast-error]:bg-bg-negative-strong-primary group-[.toast-info]:bg-bg-info-strong-primary group-[.toast-success]:bg-bg-positive-strong-primary group-[.toast-warning]:bg-bg-warning-strong-primary group-[.toast-error]:text-text-negative-strong group-[.toast-info]:text-text-info-strong group-[.toast-success]:text-text-positive-strong group-[.toast-warning]:text-text-warning-strong group-[.toast]:hover:border-transparent group-[.toast-error]:hover:bg-bg-negative-strong-secondary group-[.toast-info]:hover:bg-bg-info-strong-secondary group-[.toast-success]:hover:bg-bg-positive-strong-secondary group-[.toast-warning]:hover:bg-bg-warning-strong-secondary group-[.toast]:hover:bg-clip-padding',
  },
  variants: {
    type: {
      default: {
        toast: 'group items-start gap-3 rounded-lg border px-4 py-3 toast',
      },
      error: {
        toast:
          'toast-error group-[.toaster]:border-border-negative-subtle group-[.toaster]:bg-bg-negative-subtle group-[.toaster]:text-text-negative-subtle',
      },
      info: {
        toast:
          'toast-info group-[.toaster]:border-border-info-subtle group-[.toaster]:bg-bg-info-subtle group-[.toaster]:text-text-info-subtle',
      },
      success: {
        toast:
          'toast-success group-[.toaster]:border-border-positive-subtle group-[.toaster]:bg-bg-positive-subtle group-[.toaster]:text-text-positive-subtle',
      },
      warning: {
        toast:
          'toast-warning group-[.toaster]:border-border-warning-subtle group-[.toaster]:bg-bg-warning-subtle group-[.toaster]:text-text-warning-subtle',
      },
    },
  },
});

const {
  actionButton,
  toast,
  cancelButton,
  closeButton,
  description,
  icon,
  title,
  toaster,
} = styles();

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className={toaster()}
      theme="system"
      visibleToasts={5}
      position="bottom-right"
      icons={{
        error: <Icon name="AlertCircle" size="sm" />,
        info: <Icon name="Info" size="sm" />,
        success: <Icon name="CheckCircle2" size="sm" />,
        warning: <Icon name="AlertTriangle" size="sm" />,
      }}
      toastOptions={{
        classNames: {
          toast: toast({ type: 'default' }),
          icon: icon(),
          title: title(),
          description: description(),
          actionButton: actionButton(),
          cancelButton: cancelButton(),
          closeButton: closeButton(),
          error: toast({ type: 'error' }),
          info: toast({ type: 'info' }),
          success: toast({ type: 'success' }),
          warning: toast({ type: 'warning' }),
        },
      }}
      {...props}
    />
  );
};

const bannerToastBaseData = {
  closeButton: true,
  duration: Infinity,
  position: 'top-center' as const,
};

type BannerToastData = Omit<
  ExternalToast,
  'closeButton' | 'position' | 'duration'
>;

const bannerToast = {
  error: (message: string | React.ReactNode, data?: BannerToastData) =>
    sonnerToast.error(message, { ...bannerToastBaseData, ...(data ?? {}) }),
  info: (message: string | React.ReactNode, data?: BannerToastData) =>
    sonnerToast.info(message, { ...bannerToastBaseData, ...(data ?? {}) }),
  success: (message: string | React.ReactNode, data?: BannerToastData) =>
    sonnerToast.success(message, { ...bannerToastBaseData, ...(data ?? {}) }),
  warning: (message: string | React.ReactNode, data?: BannerToastData) =>
    sonnerToast.warning(message, { ...bannerToastBaseData, ...(data ?? {}) }),
};

type BannerToastType = keyof typeof bannerToast;

export type ToastInstance = {
  success: (
    message: string | React.ReactNode,
    data?: ExternalToast,
  ) => string | number;
  info: (
    message: string | React.ReactNode,
    data?: ExternalToast,
  ) => string | number;
  warning: (
    message: string | React.ReactNode,
    data?: ExternalToast,
  ) => string | number;
  error: (
    message: string | React.ReactNode,
    data?: ExternalToast,
  ) => string | number;
  message: (
    message: string | React.ReactNode,
    data?: ExternalToast,
  ) => string | number;
  dismiss: (id?: number | string) => string | number;
};

export const createWebToaster = (): ToastInstance => {
  return {
    success: (message, data) => sonnerToast.success(message, data),
    info: (message, data) => sonnerToast.info(message, data),
    warning: (message, data) => sonnerToast.warning(message, data),
    error: (message, data) => sonnerToast.error(message, data),
    dismiss: (id) => sonnerToast.dismiss(id),
    message: (message, data) => sonnerToast(message, data),
  };
};

const ToastContext = createContext<ToastInstance | null>(null);

export const useToast = (): ToastInstance => {
  const context = useContext(ToastContext);
  if (!context) {
    return sonnerToast;
  }
  return context;
};

export interface ToastProviderProps {
  toaster: ToastInstance;
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  toaster,
  children,
}) => {
  return (
    <ToastContext.Provider value={toaster}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
};

export { bannerToast, BannerToastType, Toaster, sonnerToast as toast };
